import React, { useState } from "react";
import { Dropdown, Spinner } from "react-bootstrap";
import html2canvas from "html2canvas-pro";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import {
  mapReservationsToExportRows,
  RESERVATION_EXPORT_HEADERS,
} from "./reservationExportUtils";

const captureTable = async (element) =>
  html2canvas(element, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: "#ffffff",
    allowTaint: true,
    scrollY: -window.scrollY,
  });

const addCanvasSliceToPdf = (
  pdf,
  sourceCanvas,
  sourceY,
  pageSourceHeight,
  pageWidth,
  pageHeight,
  padding
) => {
  const contentWidth = pageWidth - padding * 2;
  const scale = contentWidth / sourceCanvas.width;
  const sliceHeight = Math.min(pageSourceHeight, sourceCanvas.height - sourceY);

  const pageCanvas = document.createElement("canvas");
  pageCanvas.width = sourceCanvas.width;
  pageCanvas.height = sliceHeight;

  const context = pageCanvas.getContext("2d");
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
  context.drawImage(
    sourceCanvas,
    0,
    sourceY,
    sourceCanvas.width,
    sliceHeight,
    0,
    0,
    sourceCanvas.width,
    sliceHeight
  );

  const imageHeight = sliceHeight * scale;
  pdf.addImage(
    pageCanvas.toDataURL("image/png"),
    "PNG",
    padding,
    padding,
    contentWidth,
    imageHeight
  );
};

const downloadXlsx = (headers, rows, filename) => {
  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Reservations");
  XLSX.writeFile(workbook, `${filename}.xlsx`);
};

const ReservationExportButton = ({
  tableRef,
  reservations = [],
  filename = "hotel-reservations",
  disabled = false,
}) => {
  const [exporting, setExporting] = useState(false);
  const [exportType, setExportType] = useState("");

  const timestamp = new Date().toISOString().slice(0, 10);
  const exportFilename = `${filename}-${timestamp}`;

  const handlePdfExport = async () => {
    if (!tableRef?.current) {
      alert("Table not ready for export.");
      return;
    }

    if (!reservations.length) {
      alert("No reservations to export.");
      return;
    }

    setExporting(true);
    setExportType("pdf");

    const hiddenElements = tableRef.current.querySelectorAll(".export-hide");
    hiddenElements.forEach((el) => {
      el.dataset.prevDisplay = el.style.display;
      el.style.display = "none";
    });

    try {
      const pdf = new jsPDF("l", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const padding = 10;

      const canvas = await captureTable(tableRef.current);
      const contentHeight = pageHeight - padding * 2;
      const scale = (pageWidth - padding * 2) / canvas.width;
      const pageSourceHeight = Math.floor(contentHeight / scale);

      let sourceY = 0;
      let pageIndex = 0;

      while (sourceY < canvas.height) {
        if (pageIndex > 0) pdf.addPage();
        addCanvasSliceToPdf(
          pdf,
          canvas,
          sourceY,
          pageSourceHeight,
          pageWidth,
          pageHeight,
          padding
        );
        sourceY += pageSourceHeight;
        pageIndex += 1;
      }

      pdf.save(`${exportFilename}.pdf`);
    } catch (error) {
      console.error("PDF export error:", error);
      alert("Failed to export PDF. Please try again.");
    } finally {
      hiddenElements.forEach((el) => {
        el.style.display = el.dataset.prevDisplay || "";
        delete el.dataset.prevDisplay;
      });
      setExporting(false);
      setExportType("");
    }
  };

  const handleExcelExport = () => {
    if (!reservations.length) {
      alert("No reservations to export.");
      return;
    }

    setExporting(true);
    setExportType("excel");

    try {
      const rows = mapReservationsToExportRows(reservations);
      downloadXlsx(RESERVATION_EXPORT_HEADERS, rows, exportFilename);
    } catch (error) {
      console.error("Excel export error:", error);
      alert("Failed to export Excel file. Please try again.");
    } finally {
      setExporting(false);
      setExportType("");
    }
  };

  const isDisabled = disabled || exporting || !reservations.length;

  return (
    <Dropdown align="end">
      <Dropdown.Toggle
        variant="secondary"
        disabled={isDisabled}
        id="reservation-export-dropdown"
      >
        {exporting ? (
          <>
            <Spinner size="sm" animation="border" className="me-2" />
            Exporting {exportType === "pdf" ? "PDF" : "Excel"}...
          </>
        ) : (
          <>
            <i className="fa-solid fa-download me-2" />
            Export
          </>
        )}
      </Dropdown.Toggle>

      <Dropdown.Menu>
        <Dropdown.Item onClick={handlePdfExport} disabled={exporting}>
          <i className="fa-solid fa-file-pdf me-2" />
          Export as PDF
        </Dropdown.Item>
        <Dropdown.Item onClick={handleExcelExport} disabled={exporting}>
          <i className="fa-solid fa-file-excel me-2" />
          Export as Excel
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default ReservationExportButton;
